package com.unrealmi.site.components.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import com.unrealmi.site.brandGradient
import com.unrealmi.site.gradientText
import com.unrealmi.site.toSitePalette

val FooterStyle = CssStyle.base {
    Modifier
        .backgroundColor(colorMode.toSitePalette().nearBackground)
        .borderTop(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .padding(top = 3.cssRem, bottom = 2.cssRem, leftRight = 1.5.cssRem)
}

val FooterInnerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(72.cssRem)
            .display(DisplayStyle.Grid)
            .gridTemplateColumns { size(1.fr) }
            .gap(2.cssRem)
    }
    Breakpoint.MD {
        Modifier.gridTemplateColumns { size(1.5.fr); size(1.fr); size(1.fr) }
    }
}

val FooterLinkStyle = CssStyle {
    base {
        Modifier
            .color(colorMode.toSitePalette().subtle)
            .fontSize(0.9.cssRem)
            .textDecorationLine(TextDecorationLine.None)
            .transition(Transition.of("color", 150.ms))
    }
    hover { Modifier.color(colorMode.toSitePalette().brand.primary) }
}

@Composable
private fun FooterLink(path: String, text: String) {
    Link(path, text, FooterLinkStyle.toModifier(), variant = UndecoratedLinkVariant.then(UncoloredLinkVariant))
}

@Composable
private fun FooterColumn(title: String, content: @Composable () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Column(Modifier.gap(0.85.cssRem)) {
        SpanText(
            title,
            Modifier.fontSize(0.8.cssRem).letterSpacing(1.5.px).fontWeight(600).color(palette.subtle)
        )
        content()
    }
}

@Composable
fun Footer(modifier: Modifier = Modifier) {
    val palette = ColorMode.current.toSitePalette()
    Box(FooterStyle.toModifier().then(modifier), contentAlignment = Alignment.TopCenter) {
        Column(Modifier.fillMaxWidth().maxWidth(72.cssRem)) {
            Div(FooterInnerStyle.toModifier()) {
                // Brand column
                Column(Modifier.gap(0.85.cssRem)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Div(
                            Modifier
                                .size(1.8.cssRem)
                                .borderRadius(0.5.cssRem)
                                .styleModifier {
                                    property("background", brandGradient(135))
                                    property("box-shadow", "0 4px 12px rgba(124,92,255,0.4)")
                                }
                        )
                        SpanText(
                            "UnrealMi",
                            Modifier.margin(left = 0.6.cssRem).fontSize(1.15.cssRem).fontWeight(700).gradientText()
                        )
                    }
                    SpanText(
                        "上海虚米网络科技有限公司\nMake AI Simple, Make Life Better",
                        Modifier.fontSize(0.85.cssRem).color(palette.subtle).lineHeight(1.7)
                            .whiteSpace(WhiteSpace.PreLine)
                    )
                }

                // Navigation column
                FooterColumn("导航") {
                    FooterLink("/", "首页")
                    FooterLink("/#capabilities", "核心能力")
                    FooterLink("/#vision", "愿景使命")
                    FooterLink("/about", "关于我们")
                }

                // Contact column
                FooterColumn("联系") {
                    FooterLink("mailto:contact@unrealmi.com", "contact@unrealmi.com")
                    SpanText("上海 · Shanghai", Modifier.fontSize(0.9.cssRem).color(palette.subtle))
                }
            }

            // Divider + copyright
            Box(Modifier.fillMaxWidth().height(1.px).backgroundColor(palette.border).margin(top = 2.5.cssRem, bottom = 1.5.cssRem))
            SpanText(
                "© 2023–2026 上海虚米网络科技有限公司 · UnrealMi",
                Modifier.fontSize(0.82.cssRem).color(palette.subtle)
            )
        }
    }
}

// Convenience alias so we can call Div with a Modifier directly in this file.
@Composable
private fun Div(modifier: Modifier, content: @Composable () -> Unit = {}) {
    org.jetbrains.compose.web.dom.Div(attrs = modifier.toAttrs()) { content() }
}
